import React, { useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import MenuHero from "./components/MenuHero";
import MenuFilters from "./components/MenuFilters";
import CategorySection from "./components/CategorySection";
import ComboBuilder from "./components/ComboBuilder";
import CartSidebar from "./components/CartSidebar";

const Menu = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedDietary, setSelectedDietary] = useState("all");
  const [selectedSpiceLevel, setSelectedSpiceLevel] = useState("all");
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);

  const menuItems = [
    {
      id: 1,
      name: "Classic Chicken Tacos",
      category: "tacos",
      price: 149,
      originalPrice: 179,
      description:
        "Tender grilled chicken marinated in authentic Mexican spices, topped with fresh cilantro, onions, and our signature lime crema",
      image: "https://images.unsplash.com/photo-1719948515819-71265e1abb0d",
      imageAlt:
        "Three authentic Mexican chicken tacos with fresh cilantro, diced onions, lime wedges, and grilled chicken on soft corn tortillas served on rustic wooden board",
      spiceLevel: "medium",
      dietary: [],
      isPopular: true,
      isNew: false,
      ingredients:
        "Halal chicken, corn tortillas, cilantro, onions, lime, Mexican spices, sour cream",
      calories: 320,
      prepTime: 12,
      allergens: ["Dairy"],
    },
    {
      id: 2,
      name: "Spicy Beef Burrito",
      category: "burritos",
      price: 189,
      description:
        "Slow-cooked halal beef wrapped in a warm flour tortilla with Mexican rice, black beans, cheese, and fiery habanero salsa",
      image: "https://images.unsplash.com/photo-1582559482926-249abc841422",
      imageAlt:
        "Large flour tortilla burrito filled with seasoned beef, Mexican rice, black beans, melted cheese, and fresh vegetables cut in half showing colorful layers",
      spiceLevel: "hot",
      dietary: [],
      isPopular: true,
      isNew: false,
      ingredients:
        "Halal beef, flour tortilla, Mexican rice, black beans, cheese, habanero salsa, lettuce, tomatoes",
      calories: 580,
      prepTime: 15,
      allergens: ["Gluten", "Dairy"],
    },
    {
      id: 3,
      name: "Veggie Quesadilla",
      category: "quesadillas",
      price: 139,
      description:
        "Grilled flour tortilla stuffed with melted cheese, bell peppers, mushrooms, and caramelized onions, served with guacamole",
      image: "https://images.unsplash.com/photo-1673990349292-ed2dfa7988c9",
      imageAlt:
        "Golden crispy quesadilla cut into triangles showing melted cheese, colorful bell peppers, mushrooms, and onions with side of fresh guacamole and sour cream",
      spiceLevel: "mild",
      dietary: ["vegetarian"],
      isPopular: false,
      isNew: false,
      ingredients:
        "Flour tortilla, mixed cheese, bell peppers, mushrooms, onions, avocado, sour cream",
      calories: 420,
      prepTime: 10,
      allergens: ["Gluten", "Dairy"],
    },
    {
      id: 4,
      name: "Fish Tacos Supreme",
      category: "tacos",
      price: 169,
      description:
        "Crispy battered fish topped with tangy cabbage slaw, chipotle mayo, and fresh pico de gallo on soft corn tortillas",
      image: "https://images.unsplash.com/photo-1500744934005-c081fbc28aba",
      imageAlt:
        "Two crispy fish tacos with golden battered fish, purple cabbage slaw, white chipotle sauce, and fresh pico de gallo on corn tortillas with lime wedges",
      spiceLevel: "medium",
      dietary: [],
      isPopular: true,
      isNew: true,
      ingredients:
        "Halal fish, corn tortillas, cabbage, chipotle mayo, pico de gallo, lime",
      calories: 380,
      prepTime: 14,
      allergens: ["Fish", "Dairy"],
    },
    {
      id: 5,
      name: "Loaded Nachos",
      category: "nachos",
      price: 199,
      description:
        "Crispy tortilla chips piled high with seasoned beef, melted cheese, jalapeños, sour cream, and fresh guacamole",
      image: "https://images.unsplash.com/photo-1560333175-93ab231c5e3a",
      imageAlt:
        "Large platter of golden tortilla chips topped with melted cheese, seasoned ground beef, jalapeño slices, dollops of sour cream, and fresh guacamole",
      spiceLevel: "medium",
      dietary: [],
      isPopular: true,
      isNew: false,
      ingredients:
        "Tortilla chips, halal beef, mixed cheese, jalapeños, sour cream, guacamole, salsa",
      calories: 650,
      prepTime: 10,
      allergens: ["Gluten", "Dairy"],
    },
    {
      id: 6,
      name: "Vegan Black Bean Burrito",
      category: "burritos",
      price: 159,
      description:
        "Wholesome burrito packed with seasoned black beans, Mexican rice, grilled vegetables, and dairy-free chipotle sauce",
      image: "https://images.unsplash.com/photo-1729889965980-6805d097d266",
      imageAlt:
        "Whole wheat burrito filled with black beans, brown rice, grilled peppers, onions, corn, and avocado slices wrapped in flour tortilla",
      spiceLevel: "mild",
      dietary: ["vegan", "vegetarian"],
      isPopular: false,
      isNew: true,
      ingredients:
        "Flour tortilla, black beans, Mexican rice, bell peppers, corn, avocado, vegan chipotle sauce",
      calories: 480,
      prepTime: 12,
      allergens: ["Gluten"],
    },
    {
      id: 7,
      name: "Carnitas Tacos",
      category: "tacos",
      price: 159,
      originalPrice: 189,
      description:
        "Slow-braised halal pork carnitas with pickled onions, fresh cilantro, and tangy salsa verde on handmade corn tortillas",
      image: "https://images.unsplash.com/photo-1687007659692-3193c18fe2b7",
      imageAlt:
        "Three carnitas tacos with tender shredded pork, pink pickled onions, fresh cilantro, and green salsa on handmade corn tortillas",
      spiceLevel: "medium",
      dietary: [],
      isPopular: true,
      isNew: false,
      ingredients:
        "Halal pork, corn tortillas, pickled onions, cilantro, salsa verde, lime",
      calories: 340,
      prepTime: 10,
      allergens: [],
    },
    {
      id: 8,
      name: "Cheese Quesadilla",
      category: "quesadillas",
      price: 119,
      description:
        "Simple yet delicious grilled quesadilla with a blend of melted Mexican cheeses, perfect for cheese lovers",
      image: "https://images.unsplash.com/photo-1673990349292-ed2dfa7988c9",
      imageAlt:
        "Golden grilled quesadilla cut into wedges showing stretchy melted cheese blend with sides of salsa and sour cream",
      spiceLevel: "mild",
      dietary: ["vegetarian"],
      isPopular: false,
      isNew: false,
      ingredients: "Flour tortilla, mixed Mexican cheeses, butter",
      calories: 380,
      prepTime: 8,
      allergens: ["Gluten", "Dairy"],
    },
    {
      id: 9,
      name: "Horchata",
      category: "drinks",
      price: 49,
      description:
        "Traditional Mexican rice drink sweetened with cinnamon and vanilla, served ice cold",
      image: "https://images.unsplash.com/photo-1610310929634-e0fa80664a45",
      imageAlt:
        "Tall glass of creamy white horchata drink with cinnamon stick garnish and ice cubes on wooden table",
      spiceLevel: "mild",
      dietary: ["vegetarian", "vegan"],
      isPopular: true,
      isNew: false,
      ingredients: "Rice, cinnamon, vanilla, sugar, water",
      calories: 120,
      prepTime: 2,
      allergens: [],
    },
    {
      id: 10,
      name: "Jamaica (Hibiscus Tea)",
      category: "drinks",
      price: 49,
      description:
        "Refreshing hibiscus flower tea with a perfect balance of tart and sweet, served over ice",
      image: "https://images.unsplash.com/photo-1609803740516-d6369dc845a4",
      imageAlt:
        "Glass of deep red hibiscus tea with ice cubes and fresh hibiscus flower garnish on rustic wooden surface",
      spiceLevel: "mild",
      dietary: ["vegetarian", "vegan"],
      isPopular: false,
      isNew: false,
      ingredients: "Hibiscus flowers, sugar, lime, water",
      calories: 80,
      prepTime: 2,
      allergens: [],
    },
    {
      id: 11,
      name: "Churros with Chocolate",
      category: "desserts",
      price: 99,
      description:
        "Crispy fried dough pastries dusted with cinnamon sugar, served with rich Mexican chocolate dipping sauce",
      image: "https://images.unsplash.com/photo-1671797000613-e87eaa856a03",
      imageAlt:
        "Golden crispy churros dusted with cinnamon sugar arranged on white plate with small bowl of dark chocolate dipping sauce",
      spiceLevel: "mild",
      dietary: ["vegetarian"],
      isPopular: true,
      isNew: false,
      ingredients: "Flour, eggs, butter, cinnamon, sugar, chocolate, milk",
      calories: 320,
      prepTime: 8,
      allergens: ["Gluten", "Dairy", "Eggs"],
    },
    {
      id: 12,
      name: "Tres Leches Cake",
      category: "desserts",
      price: 89,
      description:
        "Light sponge cake soaked in three types of milk, topped with whipped cream and fresh strawberries",
      image: "https://images.unsplash.com/photo-1706057421906-d8a7a8d62e82",
      imageAlt:
        "Slice of moist tres leches cake with white frosting, topped with fresh strawberry and mint leaf on decorative plate",
      spiceLevel: "mild",
      dietary: ["vegetarian"],
      isPopular: true,
      isNew: false,
      ingredients:
        "Sponge cake, evaporated milk, condensed milk, heavy cream, whipped cream, strawberries",
      calories: 380,
      prepTime: 5,
      allergens: ["Gluten", "Dairy", "Eggs"],
    },
    {
      id: 13,
      name: "Shrimp Tacos",
      category: "tacos",
      price: 179,
      description:
        "Grilled jumbo shrimp seasoned with garlic and lime, topped with mango salsa and avocado crema",
      image: "https://images.unsplash.com/photo-1628259655849-bddf20ee8928",
      imageAlt:
        "Two shrimp tacos with grilled pink shrimp, colorful mango salsa, avocado cream, and fresh cilantro on corn tortillas",
      spiceLevel: "mild",
      dietary: [],
      isPopular: true,
      isNew: true,
      ingredients:
        "Halal shrimp, corn tortillas, mango, avocado, lime, garlic, cilantro",
      calories: 290,
      prepTime: 12,
      allergens: ["Shellfish", "Dairy"],
    },
    {
      id: 14,
      name: "Mexican Rice Bowl",
      category: "nachos",
      price: 129,
      description:
        "Fluffy Mexican rice topped with black beans, corn, pico de gallo, and your choice of protein",
      image: "https://images.unsplash.com/photo-1543339186-90777e2cbf52",
      imageAlt:
        "Colorful rice bowl with Mexican rice, black beans, corn kernels, diced tomatoes, avocado slices, and cilantro in white bowl",
      spiceLevel: "mild",
      dietary: ["vegetarian", "gluten-free"],
      isPopular: false,
      isNew: false,
      ingredients:
        "Mexican rice, black beans, corn, tomatoes, onions, cilantro, lime",
      calories: 380,
      prepTime: 10,
      allergens: [],
    },
    {
      id: 15,
      name: "Chicken Quesadilla Supreme",
      category: "quesadillas",
      price: 169,
      description:
        "Loaded quesadilla with grilled chicken, three cheese blend, bell peppers, and jalapeños",
      image: "https://images.unsplash.com/photo-1556560499-6d819d24d408",
      imageAlt:
        "Large quesadilla cut into triangles showing grilled chicken, melted cheese, colorful peppers, and jalapeños with sour cream and salsa",
      spiceLevel: "hot",
      dietary: [],
      isPopular: true,
      isNew: false,
      ingredients:
        "Halal chicken, flour tortilla, mixed cheeses, bell peppers, jalapeños, sour cream",
      calories: 520,
      prepTime: 12,
      allergens: ["Gluten", "Dairy"],
    },
  ];

  const filteredItems = useMemo(() => {
    return menuItems?.filter((item) => {
      const matchesSearch =
        item?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        item?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
      const matchesCategory =
        selectedCategory === "all" || item?.category === selectedCategory;
      const matchesDietary =
        selectedDietary === "all" || item?.dietary?.includes(selectedDietary);
      const matchesSpice =
        selectedSpiceLevel === "all" || item?.spiceLevel === selectedSpiceLevel;

      return matchesSearch && matchesCategory && matchesDietary && matchesSpice;
    });
  }, [searchQuery, selectedCategory, selectedDietary, selectedSpiceLevel]);

  const groupedItems = useMemo(() => {
    const groups = {};
    filteredItems?.forEach((item) => {
      if (!groups?.[item?.category]) {
        groups[item.category] = [];
      }
      groups?.[item?.category]?.push(item);
    });
    return groups;
  }, [filteredItems]);

  const handleClearFilters = () => {
    setSearchQuery("");
    setSelectedCategory("all");
    setSelectedDietary("all");
    setSelectedSpiceLevel("all");
  };

  const handleAddToCart = (item) => {
    setCartItems((prevItems) => {
      const existingItem = prevItems?.find((i) => i?.id === item?.id);
      if (existingItem) {
        return prevItems?.map((i) =>
          i?.id === item?.id
            ? { ...i, quantity: i?.quantity + item?.quantity }
            : i
        );
      }
      return [...prevItems, item];
    });
    setIsCartOpen(true);
  };

  const handleAddCombo = (combo) => {
    console.log("Combo added:", combo);
    setIsCartOpen(true);
  };

  const handleUpdateQuantity = (itemId, newQuantity) => {
    setCartItems((prevItems) =>
      prevItems?.map((item) =>
        item?.id === itemId ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  const handleRemoveItem = (itemId) => {
    setCartItems((prevItems) =>
      prevItems?.filter((item) => item?.id !== itemId)
    );
  };

  return (
    <>
      <Helmet>
        <title>
          Menu - Taco Street Digital | Authentic Mexican Street Food
        </title>
        <meta
          name="description"
          content="Explore our authentic Mexican street food menu featuring tacos, burritos, quesadillas, and more. 100% halal certified and made fresh to order."
        />
      </Helmet>
      <Header />
      <main className="main-content bg-[var(--color-background)]">
        <MenuHero />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <ComboBuilder onAddCombo={handleAddCombo} />

          <div className="mt-12">
            <MenuFilters
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              selectedDietary={selectedDietary}
              setSelectedDietary={setSelectedDietary}
              selectedSpiceLevel={selectedSpiceLevel}
              setSelectedSpiceLevel={setSelectedSpiceLevel}
              onClearFilters={handleClearFilters}
            />
          </div>

          <div className="mt-12">
            {Object.keys(groupedItems)?.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-24 h-24 rounded-full bg-[var(--color-muted)] flex items-center justify-center mx-auto mb-4">
                  <Icon
                    name="Search"
                    size={48}
                    color="var(--color-muted-foreground)"
                  />
                </div>
                <h3 className="text-xl font-semibold text-[var(--color-foreground)] mb-2">
                  No items found
                </h3>
                <p className="text-[var(--color-muted-foreground)] mb-4">
                  Try adjusting your filters or search query
                </p>
                <Button variant="default" onClick={handleClearFilters}>
                  Clear Filters
                </Button>
              </div>
            ) : (
              Object.entries(groupedItems)?.map(([category, items]) => (
                <CategorySection
                  key={category}
                  category={category}
                  items={items}
                  onAddToCart={handleAddToCart}
                />
              ))
            )}
          </div>
        </div>

        {/* <div className="fixed bottom-6 right-6 z-30">
          <button
            onClick={() => setIsCartOpen(true)}
            className="relative w-16 h-16 rounded-full bg-[var(--color-primary)] shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 flex items-center justify-center"
            aria-label="Open cart"
          >
            <Icon name="ShoppingCart" size={24} color="#FFFFFF" />
            {cartItems?.length > 0 && (
              <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-[var(--color-accent)] flex items-center justify-center shadow-md">
                <span className="text-xs font-bold text-white">
                  {cartItems?.length}
                </span>
              </div>
            )}
          </button>
        </div> */}
      </main>
      <Footer />
      {/* <CartSidebar
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cartItems={cartItems}
        onUpdateQuantity={handleUpdateQuantity}
        onRemoveItem={handleRemoveItem} /> */}
    </>
  );
};

export default Menu;
